import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { pathgen } from 'object-path-generator';
import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router';
import { ConfirmationModal, EnhancedAutocomplete, Spinner, useOverlayContext } from 'seagull';
import { useAgreementTriageModal } from '~/components/AgreementTriageModal/AgreementTriageModalOpener';
import { CardButtonGrid } from '~/components/CardButtonGrid/CardButtonGrid';
import { ImportAgreementModal } from '~/components/ImportAgreementModal/ImportAgreementModal';
import { routes } from '~/constants';
import { useRedirectToProposal } from '~/hooks/useCreateAgreement';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { useRootDevice } from '~/providers/DeviceProvider';
import { genericConnector } from '~/utils/react-hook-form.utils';
import { useProposalTemplates } from '../../hooks/reactQuery/queries/useProposalTemplates';
import { useCreateDraft } from './hooks/useCreateDraft';
import s from './SelectProposalTypeModal.module.scss';
export const SelectProposalTypeModalTestIds = pathgen('SelectProposalTypeModalTestIds');
export const SelectProposalTypeModal = (param)=>{
    let { dismissModal, proposalType, navigationState, onImportAgreement } = param;
    const formId = 'create-proposal-from-template';
    const [selectedProposalType, setSelectedProposalType] = useState(proposalType);
    const { openCreationModal } = useRedirectToProposal();
    const { t: selectTexts } = useLocaleKeys('components.selectProposalTypeModal');
    const { templates, isLoading } = useProposalTemplates({
        refreshOnMount: true
    });
    const { createDraft, isInProgress } = useCreateDraft({
        onSuccess: dismissModal
    });
    const { isMobileDevice } = useRootDevice();
    const { pushBottomSheetPage, hideBottomSheet } = useOverlayContext();
    const { open: openAgreementTriage } = useAgreementTriageModal();
    const { AGREEMENT_FROM_PDF } = useFeatureFlags();
    const { t } = useLocaleKeys();
    const history = useHistory();
    const { control, handleSubmit } = useForm({
        mode: 'all',
        defaultValues: {
            proposalTemplateId: ''
        }
    });
    const showImportCard = AGREEMENT_FROM_PDF && (onImportAgreement != null || isMobileDevice);
    const proposalTypes = useMemo(()=>[
            {
                title: selectTexts.useTemplate(),
                content: selectTexts.useTemplateContent(),
                onClick: ()=>{
                    setSelectedProposalType('template');
                },
                testId: SelectProposalTypeModalTestIds.ProposalType.Template(),
                bi: {
                    name: 'select_proposal_type_template'
                }
            },
            {
                title: selectTexts.startFromScratch(),
                content: selectTexts.startFromScratchContent(),
                onClick: ()=>{
                    setSelectedProposalType('draft');
                    openCreationModal({
                        state: navigationState
                    });
                    dismissModal();
                },
                testId: SelectProposalTypeModalTestIds.ProposalType.Draft(),
                bi: {
                    name: 'select_proposal_type_draft'
                }
            },
            ...showImportCard ? [
                {
                    title: t.agreementImport.menuItem.title(),
                    content: t.agreementImport.menuItem.body(),
                    onClick: ()=>{
                        if (isMobileDevice) {
                            pushBottomSheetPage({
                                content: /*#__PURE__*/ _jsx(ImportAgreementModal, {
                                    dismissModal: hideBottomSheet,
                                    onSuccess: (extraction, conversationId)=>{
                                        hideBottomSheet();
                                        openAgreementTriage(conversationId, extraction);
                                    }
                                })
                            });
                        } else {
                            dismissModal();
                            onImportAgreement === null || onImportAgreement === void 0 ? void 0 : onImportAgreement();
                        }
                    },
                    testId: SelectProposalTypeModalTestIds.ProposalType.ImportAgreement(),
                    bi: {
                        name: 'select_proposal_type_import_agreement'
                    }
                }
            ] : []
        ], [
        showImportCard,
        isMobileDevice
    ]);
    let primaryButton = '';
    let cancelButton = '';
    let title = selectTexts.createProposal();
    if (selectedProposalType === 'template') {
        primaryButton = selectTexts.createProposal();
        cancelButton = t.common.buttons.cancel();
        title = selectTexts.chooseTemplate();
    }
    const createProposalFromTemplate = async (param)=>{
        let { proposalTemplateId } = param;
        if (proposalTemplateId) {
            createDraft(proposalTemplateId);
        }
    };
    const renderTypeSelection = ()=>/*#__PURE__*/ _jsx(CardButtonGrid, {
            testId: SelectProposalTypeModalTestIds.ProposalType.Root(),
            items: proposalTypes.map((type)=>({
                    title: type.title,
                    description: type.content,
                    testId: type.testId,
                    bi: type.bi,
                    onClick: type.onClick
                }))
        });
    const renderTemplateSelection = ()=>/*#__PURE__*/ _jsx(Controller, {
            control: control,
            name: "proposalTemplateId",
            rules: {
                required: selectTexts.pleaseSelectTemplate()
            },
            render: (renderProps)=>{
                const { showErrors, errorMsg } = genericConnector(renderProps);
                return /*#__PURE__*/ _jsx(EnhancedAutocomplete, {
                    bi: {
                        name: 'proposal_template'
                    },
                    options: templates !== null && templates !== void 0 ? templates : [],
                    isLoading: isLoading,
                    className: s.serviceTemplateSelector,
                    keyField: "id",
                    labelField: "title",
                    textFieldProps: {
                        bi: {
                            name: 'proposal_template_name'
                        },
                        label: selectTexts.templateName(),
                        placeholder: selectTexts.chooseTemplateSubtitle(),
                        error: showErrors,
                        helperText: errorMsg
                    },
                    applyOptionChange: (option)=>{
                        const { field } = renderProps;
                        field.onChange(option === null || option === void 0 ? void 0 : option.id);
                    },
                    newItemOption: {
                        text: selectTexts.createNewTemplate(),
                        onClick: ()=>{
                            history.push(routes.proposalTemplateStudio);
                            return Promise.resolve();
                        }
                    },
                    emptySearchText: selectTexts.emptySearchText(),
                    testId: SelectProposalTypeModalTestIds.ServiceTemplateSelector()
                });
            }
        });
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        bi: {
            primaryButton: {
                name: 'select_proposal_type_confirm'
            },
            secondaryButton: {
                name: 'select_proposal_type_cancel'
            }
        },
        showFooter: selectedProposalType === 'template',
        className: s.confirmationModal,
        contentClassName: s.confirmationModalContent,
        style: {
            padding: '0'
        },
        width: "450px",
        title: title,
        testId: SelectProposalTypeModalTestIds.Root(),
        primaryButtonTestId: SelectProposalTypeModalTestIds.Buttons.CreateProposal(),
        textAlignCenter: true,
        onPrimaryButtonClick: handleSubmit(createProposalFromTemplate),
        onCloseButtonClick: dismissModal,
        cancelButtonDisabled: false,
        primaryButtonLabel: isInProgress ? /*#__PURE__*/ _jsx(Spinner, {
            size: 40
        }) : primaryButton,
        primaryButtonDisabled: isInProgress,
        primaryButtonFormId: formId,
        secondaryButtonLabel: cancelButton,
        secondaryButtonTestId: SelectProposalTypeModalTestIds.Buttons.Cancel(),
        onSecondaryButtonClick: dismissModal,
        content: /*#__PURE__*/ _jsx("form", {
            id: formId,
            onSubmit: handleSubmit(createProposalFromTemplate),
            children: selectedProposalType === 'template' ? renderTemplateSelection() : renderTypeSelection()
        })
    });
};
